import * as React from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { NotificationCategory, type UserNotificationOverrides } from '../types';
import type { JourneyDefinition } from '../types';

// Ranges carry a stable `id` used only as a React key. Without it, removing a
// range from the middle of the list (filter-by-index) causes the surviving
// rows to re-key, which breaks any transient per-item state (e.g., focus,
// mid-typing validation). The id never leaves the component.
interface RangeRow {
  id: string;
  start: number;
  end: number;
}

interface OverrideFormValues {
  mutedUntil: string;
  mutedCategories: NotificationCategory[];
  mutedJourneys: string[];
  notes: string;
  forcedWindowExpiresAt: string;
  forcedWindowRanges: RangeRow[];
}

let rangeIdCounter = 0;
function nextRangeId(): string {
  rangeIdCounter += 1;
  return `range-${rangeIdCounter}`;
}

export interface OverrideFormProps {
  initial?: UserNotificationOverrides | null;
  journeys: JourneyDefinition[];
  submitting?: boolean;
  onSubmit: (payload: {
    reason: string;
    overrides: Partial<UserNotificationOverrides>;
  }) => Promise<void>;
  onCancel?: () => void;
}

export function OverrideForm({ initial, journeys, submitting, onSubmit, onCancel }: OverrideFormProps) {
  const [values, setValues] = React.useState<OverrideFormValues>(() => toForm(initial));
  const [reason, setReason] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  // Resync when `initial` identity changes (user switched, or a save
  // refetched the profile). Without this, the form retains stale values
  // from the previously-inspected user.
  const initialRef = React.useRef(initial);
  React.useEffect(() => {
    if (initial !== initialRef.current) {
      initialRef.current = initial;
      setValues(toForm(initial));
      setReason('');
      setError(null);
    }
  }, [initial]);

  const canSubmit = reason.trim().length > 0 && !submitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    const overrides = toOverrides(values);
    try {
      await onSubmit({ reason: reason.trim(), overrides });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="override-muted-until">Muted until (UTC ISO)</Label>
        <Input
          id="override-muted-until"
          type="datetime-local"
          value={toLocalDatetime(values.mutedUntil)}
          onChange={(e) => setValues({ ...values, mutedUntil: fromLocalDatetime(e.target.value) })}
        />
        <p className="text-xs text-muted-foreground">
          Auto-clears at this time. Leave empty to not set.
        </p>
      </div>

      <div className="space-y-1.5">
        <Label>Muted categories</Label>
        <div className="flex flex-wrap gap-2">
          {Object.values(NotificationCategory).map((cat) => {
            const active = values.mutedCategories.includes(cat);
            return (
              <button
                key={cat}
                type="button"
                onClick={() =>
                  setValues({
                    ...values,
                    mutedCategories: active
                      ? values.mutedCategories.filter((c) => c !== cat)
                      : [...values.mutedCategories, cat],
                  })
                }
                className={
                  active
                    ? 'rounded-full border border-primary bg-primary px-3 py-1 text-xs font-medium text-primary-foreground'
                    : 'rounded-full border px-3 py-1 text-xs font-medium hover:bg-accent'
                }
                aria-pressed={active}
              >
                {cat}
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>Muted journeys</Label>
        <div className="flex items-center gap-2">
          <Select
            onValueChange={(journeyId) => {
              if (!values.mutedJourneys.includes(journeyId)) {
                setValues({ ...values, mutedJourneys: [...values.mutedJourneys, journeyId] });
              }
            }}
            value=""
          >
            <SelectTrigger className="max-w-[260px]">
              <SelectValue placeholder="Add a journey…" />
            </SelectTrigger>
            <SelectContent>
              {journeys
                .filter((j) => !values.mutedJourneys.includes(j.journeyId))
                .map((j) => (
                  <SelectItem key={j.journeyId} value={j.journeyId}>
                    {j.name} <span className="text-muted-foreground">· {j.journeyId}</span>
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {values.mutedJourneys.map((id) => (
            <Badge key={id} variant="muted" className="gap-1">
              <span className="font-mono text-[11px]">{id}</span>
              <button
                type="button"
                aria-label={`Remove ${id}`}
                onClick={() =>
                  setValues({ ...values, mutedJourneys: values.mutedJourneys.filter((j) => j !== id) })
                }
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      </div>

      <Separator />

      <div className="space-y-2">
        <Label>Forced window (per-day hour ranges)</Label>
        <div className="space-y-2">
          {values.forcedWindowRanges.map((range, i) => (
            <div key={range.id} className="flex items-center gap-2">
              <Input
                type="number"
                min={0}
                max={23}
                className="w-20"
                aria-label="Start hour"
                value={range.start}
                onChange={(e) => {
                  const v = Math.max(0, Math.min(23, Number(e.target.value)));
                  const next = [...values.forcedWindowRanges];
                  next[i] = { ...next[i], start: v };
                  setValues({ ...values, forcedWindowRanges: next });
                }}
              />
              <span className="text-xs text-muted-foreground">→</span>
              <Input
                type="number"
                min={0}
                max={23}
                className="w-20"
                aria-label="End hour"
                value={range.end}
                onChange={(e) => {
                  const v = Math.max(0, Math.min(23, Number(e.target.value)));
                  const next = [...values.forcedWindowRanges];
                  next[i] = { ...next[i], end: v };
                  setValues({ ...values, forcedWindowRanges: next });
                }}
              />
              <Button
                type="button"
                size="icon"
                variant="ghost"
                aria-label="Remove range"
                onClick={() =>
                  setValues({
                    ...values,
                    forcedWindowRanges: values.forcedWindowRanges.filter((_, j) => j !== i),
                  })
                }
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() =>
              setValues({
                ...values,
                forcedWindowRanges: [
                  ...values.forcedWindowRanges,
                  { id: nextRangeId(), start: 9, end: 11 },
                ],
              })
            }
          >
            Add range
          </Button>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="forced-expires">Forced window expires at</Label>
          <Input
            id="forced-expires"
            type="datetime-local"
            value={toLocalDatetime(values.forcedWindowExpiresAt)}
            onChange={(e) =>
              setValues({ ...values, forcedWindowExpiresAt: fromLocalDatetime(e.target.value) })
            }
            disabled={values.forcedWindowRanges.length === 0}
          />
          <p className="text-xs text-muted-foreground">
            Required when ranges are set. Indefinite forced windows are forbidden.
          </p>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="override-notes">Notes</Label>
        <Textarea
          id="override-notes"
          rows={2}
          value={values.notes}
          onChange={(e) => setValues({ ...values, notes: e.target.value })}
          placeholder="Support context surfaced in the inspector."
          maxLength={1000}
        />
      </div>

      <Separator />

      <div className="space-y-1.5">
        <Label htmlFor="override-reason">Reason (required)</Label>
        <Textarea
          id="override-reason"
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why are you setting this override?"
          maxLength={500}
          required
        />
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="flex items-center justify-end gap-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={!canSubmit}>
          {submitting ? 'Saving…' : 'Save override'}
        </Button>
      </div>
    </form>
  );
}

function toForm(initial?: UserNotificationOverrides | null): OverrideFormValues {
  return {
    mutedUntil: initial?.mutedUntil ?? '',
    mutedCategories: initial?.mutedCategories ?? [],
    mutedJourneys: initial?.mutedJourneys ?? [],
    notes: initial?.notes ?? '',
    forcedWindowExpiresAt: initial?.forcedWindow?.expiresAt ?? '',
    // Attach a fresh stable id to each hydrated range. Ids are component-local
    // and not persisted — stripped out in `toOverrides`.
    forcedWindowRanges: (initial?.forcedWindow?.ranges ?? []).map((r) => ({
      id: nextRangeId(),
      start: r.start,
      end: r.end,
    })),
  };
}

function toOverrides(v: OverrideFormValues): Partial<UserNotificationOverrides> {
  const out: Partial<UserNotificationOverrides> = {};
  if (v.mutedUntil) out.mutedUntil = v.mutedUntil;
  if (v.mutedCategories.length > 0) out.mutedCategories = v.mutedCategories;
  if (v.mutedJourneys.length > 0) out.mutedJourneys = v.mutedJourneys;
  if (v.notes) out.notes = v.notes;
  if (v.forcedWindowRanges.length > 0 && v.forcedWindowExpiresAt) {
    out.forcedWindow = {
      // Strip the component-local `id` before sending to the backend —
      // it's a React key, not part of the override contract.
      ranges: v.forcedWindowRanges.map(({ start, end }) => ({ start, end })),
      expiresAt: v.forcedWindowExpiresAt,
    };
  }
  return out;
}

function toLocalDatetime(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes(),
  )}`;
}

function fromLocalDatetime(local: string): string {
  if (!local) return '';
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
}
